import axios, { Axios, AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import {
  ApiEndpoints,
  API_BASE_URL,
  API_OAUTH_CLIENT_ID,
  API_OAUTH_CLIENT_SECRET,
} from "../constants";
import {
  AccessToken,
  Credentials,
  LogInRequestData,
  OAuthRefreshTokenData,
  OAuthResponseData,
  RefreshToken,
  UrlEncodedString,
} from "./types";

export const ACCESS_TOKEN_LOCALSTORAGE_KEY = "access_token";
export const REFRESH_TOKEN_LOCALSTORAGE_KEY = "refresh_token";
export const walixApiClient = axios.create({ baseURL: API_BASE_URL });

function jsonToUrlEncoded(element: any, key?: string, list_arg?: any): UrlEncodedString {
  // https://gist.github.com/lastguest/1fd181a9c9db0550a847
  const list = list_arg || [];
  if (typeof element == "object") {
    for (var idx in element)
      jsonToUrlEncoded(element[idx], key ? key + "[" + idx + "]" : idx, list);
  } else {
    list.push(key + "=" + encodeURIComponent(element));
  }
  return list.join("&");
}

export class CredentialsManager {
  constructor(private readonly client: Axios, private readonly storage: Storage) {}

  public logIn = async ({ username, password }: Credentials): Promise<boolean> => {
    const data: LogInRequestData = {
      grant_type: "password",
      username: username,
      password: password,
    };
    const config: AxiosRequestConfig = {
      auth: {
        username: API_OAUTH_CLIENT_ID,
        password: API_OAUTH_CLIENT_SECRET,
      },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    let response: AxiosResponse<OAuthResponseData>;

    try {
      response = await this.client.post<
        UrlEncodedString,
        AxiosResponse<OAuthResponseData>
      >(ApiEndpoints.OAUTH_TOKEN, jsonToUrlEncoded(data), config);
    } catch (error: unknown) {
      console.debug("Authentication failed");
      this.clearOAuthTokens();
      return false;
    }

    const { access_token, refresh_token } = response.data;
    console.debug("Authentication succeeded");

    console.debug("Storing API tokens in browser...");
    this.saveAccessToken(access_token);
    this.saveRefreshToken(refresh_token);
    return true;
  };

  public validateStoredCredentials = async (): Promise<boolean> => {
    // this function must only be executed once: on SPA load
    console.debug("Checking if there are any stored credentials...");
    const accessToken = this.readAccessToken();
    if (accessToken) {
      console.debug("Stored credentials found");
      console.debug("Validating stored credentials...");
      const isValid = await this.validateAccessToken(accessToken);

      if (isValid) {
        console.debug("Stored credentials are valid");
        return true;
      }

      const refreshToken = this.readRefreshToken();
      if (refreshToken) {
        console.debug("Attempting to refresh credentials...");
        const newAccessToken = await this.refreshAccessTokenWith(refreshToken);
        if (newAccessToken) {
          console.debug("Credentials refreshed");
          this.saveAccessToken(newAccessToken);
          return true;
        } else {
          console.debug("It was not possible to refresh credentials");
        }
      }

      console.debug("Stored credentials are not valid");
      console.debug("Clearing stored credentials...");
      this.clearOAuthTokens();
      return false;
    }

    console.debug("No credentials found");
    this.removeRefreshToken();
    return false;
  };

  public readCredentialsFromLocalStorage(): AccessToken | null {
    return this.readAccessToken();
  }

  public userIsAuthenticated(): boolean {
    const accessToken = this.readAccessToken();
    return !!accessToken;
  }

  private validateAccessToken = async (token: AccessToken): Promise<boolean> => {
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    let response: AxiosResponse;
    try {
      response = await this.client.get(ApiEndpoints.OAUTH_VALIDATE_TOKEN, config);
    } catch (error: unknown) {
      console.error(error);
      if ((error as AxiosError).response?.status === 401) {
        return false;
      }

      throw error;
    }
    return response.status === 200 ? true : false;
  };

  private async refreshAccessTokenWith(
    refreshToken: RefreshToken,
  ): Promise<AccessToken | null> {
    const data: OAuthRefreshTokenData = {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: API_OAUTH_CLIENT_ID,
      client_secret: API_OAUTH_CLIENT_SECRET,
    };
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    let response: AxiosResponse<OAuthResponseData>;
    try {
      response = await this.client.post<
        UrlEncodedString,
        AxiosResponse<OAuthResponseData>
      >(ApiEndpoints.OAUTH_TOKEN, jsonToUrlEncoded(data), config);
    } catch (error: unknown) {
      console.error(error);
      return null;
    }

    const { access_token, refresh_token } = response.data;
    console.debug("Credentials succeesfully refreshed");

    console.debug("Storing API tokens in browser...");
    this.saveAccessToken(access_token);
    this.saveRefreshToken(refresh_token);

    return access_token;
  }

  private clearOAuthTokens(): void {
    console.debug("Clearing API tokens from browser...");
    this.removeAccessToken();
    this.removeRefreshToken();
  }

  private saveAccessToken(token: AccessToken): void {
    this.storage.setItem(ACCESS_TOKEN_LOCALSTORAGE_KEY, token);
  }
  private readAccessToken(): AccessToken | null {
    const token = this.readItem(ACCESS_TOKEN_LOCALSTORAGE_KEY);
    return token;
  }
  private removeAccessToken(): void {
    this.storage.removeItem(ACCESS_TOKEN_LOCALSTORAGE_KEY);
  }

  private saveRefreshToken(token: RefreshToken): void {
    this.storage.setItem(REFRESH_TOKEN_LOCALSTORAGE_KEY, token);
  }
  private readRefreshToken(): RefreshToken | null {
    const token = this.readItem(REFRESH_TOKEN_LOCALSTORAGE_KEY);
    return token;
  }
  private removeRefreshToken(): void {
    this.storage.removeItem(REFRESH_TOKEN_LOCALSTORAGE_KEY);
  }

  private readItem(key: string): string | null {
    const value = this.storage.getItem(key);
    if (!value) {
      return null;
    }
    return value;
  }
}

export const credentialsManager = new CredentialsManager(
  walixApiClient,
  window.localStorage,
);
